import { lazy } from 'react'
import { useSelector } from 'react-redux'

import { useFeatureValue } from '@growthbook/growthbook-react'
import { Spinner } from '~/shared/components/Spinner'
import {
  FEATURE_KEYS,
  SubscriptionListType,
  SubscriptionTag,
} from '~/shared/constants/commonConstants'
import { PageId } from '~/shared/constants/pagesConstants'
import { useGetSubscriptions } from '~/shared/hooks/useGetSubscriptions'
import { ScreenName } from '~/shared/services/eventLogger'
import { selectAreSubscriptionsReady } from '~/shared/store/payment/selects'
import { PageProps, TIdToPageMap } from '~/shared/types/commonTypes'

const Upsell = lazy(() => import('./v1/Upsell'))
const UpsellV3 = lazy(() => import('./v3/Upsell'))
const UpsellTrial = lazy(() => import('./v4/Upsell'))
const UpsellRecurringUs = lazy(() => import('./v5/Upsell'))
const UpsellOneTimeUs = lazy(() => import('./v6/Upsell'))

const UPSELL_PAGE_MAP: TIdToPageMap = {
  [PageId.UPSELL_1]: Upsell,
}

const UpsellPage = (props: PageProps) => {
  const areSubscriptionsReady = useSelector(selectAreSubscriptionsReady)
  const upsellV3Variant = useFeatureValue(
    FEATURE_KEYS.upsell_v3_anatomy_en,
    'control',
  )
  const upcellVariant = useFeatureValue(FEATURE_KEYS.upcell, 'control')
  const newPriceEurVariant = useFeatureValue(
    FEATURE_KEYS.price_europe_eur,
    'control',
  )
  const newPriceEnVariant = useFeatureValue(
    FEATURE_KEYS.new_price_en,
    'control',
  )
  const newPriceBrVariant = useFeatureValue(
    FEATURE_KEYS.new_price_br,
    'control',
  )
  const newPriceMxVariant = useFeatureValue(
    FEATURE_KEYS.new_price_mx,
    'control',
  )
  const newPriceTrVariant = useFeatureValue(
    FEATURE_KEYS.new_price_tr,
    'control',
  )

  const PageComponent = UPSELL_PAGE_MAP[props.pageId]
  const isCardVariant = upsellV3Variant === 'toggle'
  const isTrialVariant = upsellV3Variant === 'trial'
  const isRecurringUsVariant = upcellVariant === 'recurring-us'
  const isOneTimeUsVariant = upcellVariant === 'one-time-us'
  const isEurPriceTestVariant = newPriceEurVariant === 'test'
  const isEnPriceTestVariant = newPriceEnVariant === 'test'
  const isBrPriceTestVariant = newPriceBrVariant === 'test'
  const isMxPriceTestVariant = newPriceMxVariant === 'test'
  const isTrPriceTestVariant = newPriceTrVariant === 'test'

  const subscriptionTags = isEurPriceTestVariant
    ? [SubscriptionTag.NO_TAX_EUR]
    : [SubscriptionTag.NO_TAX]
  if (isCardVariant) subscriptionTags.push(SubscriptionTag.TOGGLE)
  if (isTrialVariant) subscriptionTags.push(SubscriptionTag.TRIAL)
  if (isRecurringUsVariant) subscriptionTags.push(SubscriptionTag.RECURRING_US)
  if (isOneTimeUsVariant) subscriptionTags.push(SubscriptionTag.ONE_TIME_US)
  if (isEnPriceTestVariant) subscriptionTags.push(SubscriptionTag.EN)
  if (isBrPriceTestVariant) subscriptionTags.push(SubscriptionTag.BR)
  if (isMxPriceTestVariant) subscriptionTags.push(SubscriptionTag.MX)
  if (isTrPriceTestVariant) subscriptionTags.push(SubscriptionTag.TR)

  useGetSubscriptions({
    tags: subscriptionTags.join(','),
    screenName: ScreenName.UPSELL_OFFER,
    subscriptionType: isOneTimeUsVariant
      ? SubscriptionListType.ONE_TIME
      : SubscriptionListType.SECONDARY,
  })

  if (!areSubscriptionsReady) return <Spinner />

  if (isTrialVariant) {
    return <UpsellTrial />
  }

  if (isRecurringUsVariant) {
    return <UpsellRecurringUs />
  }

  if (isOneTimeUsVariant) {
    return <UpsellOneTimeUs />
  }

  if (isCardVariant) {
    return <UpsellV3 />
  }

  return <PageComponent {...props} />
}

export default UpsellPage
